import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const GOLD = [212, 175, 55];
const DARK = [16, 20, 34];
const GREY = [130, 130, 130];

export const SOURCE_LABEL = {
  receiving: "Incoming Shipment", stockroom: "Stockroom / Inventory",
  display: "Store Display", customer: "Customer Damage",
  employee_handling: "Employee Handling", other: "Other",
};
export const SEVERITY_LABEL = { minor: "Minor", moderate: "Moderate", major: "Major", total_loss: "Total Loss" };
export const STATUS_LABEL = {
  reported: "Reported", under_review: "Under Review", action_required: "Action Required",
  approved_for_disposal: "Approved for Disposal", return_to_supplier: "Return to Supplier",
  markdown_approved: "Markdown Approved", transferred: "Transferred",
  resolved: "Resolved", closed: "Closed",
};
export const DISPOSITION_LABEL = {
  "": "—", return_to_supplier: "Return to Supplier", dispose: "Dispose / Destroy",
  discount: "Sell at Discount", keep_display: "Keep for Display", repair: "Repair",
  parts: "Use for Parts", transfer_damage_area: "Transfer to Damage Area",
  claim_carrier: "Claim With Carrier", claim_supplier: "Claim With Supplier", other: "Other",
};

async function photoToDataUrl(p) {
  if (!p) return null;
  if (p.data_url) return p.data_url;
  if (!p.url) return null;
  try {
    const token = window.localStorage.getItem("hos_token");
    const abs = p.url.startsWith("http") ? p.url : `${window.location.origin}${p.url}`;
    const resp = await fetch(abs, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

const fmt = (i) => { try { return i ? new Date(i).toLocaleString() : "—"; } catch { return i || "—"; } };

export default async function generateDamagePdf(d, opts = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...DARK); doc.rect(0, 0, w, 26, "F");
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text("HOUSE OF SPELLS — DAMAGE REPORT", 12, 12);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(`${d.reference_number} · ${d.store_name || ""} · ${fmt(d.created_at)}`, 12, 19);

  let y = 34;
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text(d.product_name || SOURCE_LABEL[d.source_type] || "Damage", 12, y); y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(`${STATUS_LABEL[d.status] || d.status} · ${SEVERITY_LABEL[d.severity] || d.severity}`, 12, y); y += 6;

  const details = [
    ["Source", SOURCE_LABEL[d.source_type] || d.source_type],
    ["Store", d.store_name || "—"],
    ["Reported By", d.reported_by_name || "—"],
    ["Product", d.product_name || "—"],
    ["SKU", d.sku || "—"],
    ["Barcode", d.barcode || "—"],
    ["Fandom", d.fandom_name || "—"],
    ["Zone", d.zone_name || d.display_zone_name || "—"],
    ["Quantity", String(d.quantity || 0)],
    ["Damage Type", d.damage_type || "—"],
    ["Severity", SEVERITY_LABEL[d.severity] || d.severity],
    ["Cause", d.cause || "—"],
    ["Sellability", d.sellability_status || "—"],
    ["Disposition", DISPOSITION_LABEL[d.disposition || ""] || d.disposition || "—"],
    ["Status", STATUS_LABEL[d.status] || d.status],
  ];
  if (d.source_type === "receiving") {
    details.push(["Receiving Ref", d.receiving_reference || "—"]);
    details.push(["Supplier", d.supplier_name || "—"]);
    details.push(["PO", d.purchase_order_number || "—"]);
    details.push(["Pallet", d.receiving_pallet_number ? `Pallet ${d.receiving_pallet_number}` : "—"]);
  }
  if (d.source_type === "display") {
    details.push(["Display Check", d.display_check_id || "—"]);
    details.push(["Display Zone", d.display_zone_name || "—"]);
    details.push(["Display Fandom", d.display_fandom_name || "—"]);
  }
  if (opts.includeCosts && (d.unit_cost != null || d.retail_value != null)) {
    if (d.unit_cost != null) details.push(["Unit Cost", `$${Number(d.unit_cost).toFixed(2)}`]);
    if (d.retail_value != null) details.push(["Retail Value", `$${Number(d.retail_value).toFixed(2)}`]);
    if (d.estimated_damage_cost != null) details.push(["Estimated Damage Cost", `$${Number(d.estimated_damage_cost).toFixed(2)}`]);
  }
  autoTable(doc, {
    startY: y, head: [["Field", "Value"]], body: details,
    theme: "grid", styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: DARK, textColor: GOLD },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 45 } },
    margin: { left: 12, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 6;

  if (d.description) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...DARK);
    doc.text("Description", 12, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(60, 60, 60);
    const wrapped = doc.splitTextToSize(d.description, w - 24);
    doc.text(wrapped, 12, y); y += wrapped.length * 4 + 4;
  }

  const photos = d.photos || [];
  if (photos.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...DARK);
    doc.text("Damage Photos", 12, 20);
    let cy = 28, col = 0; const cx = 12; const size = 60;
    for (const p of photos) {
      const url = await photoToDataUrl(p);
      if (!url) continue;
      const x = cx + col * (size + 6);
      if (cy + size > doc.internal.pageSize.getHeight() - 20) { doc.addPage(); cy = 20; col = 0; }
      try { doc.addImage(url, "JPEG", x, cy, size, size, undefined, "FAST"); } catch {}
      col += 1;
      if (col >= 3) { col = 0; cy += size + 6; }
    }
  }

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    doc.setTextColor(...GREY); doc.setFontSize(8);
    doc.text(`Page ${i} of ${total}`, w - 25, h - 8);
    doc.text(`Generated ${new Date().toLocaleString()}`, 12, h - 8);
  }
  const filename = `House_of_Spells_Damage_${d.reference_number}.pdf`;
  doc.save(filename);
  return filename;
}
